import { useEffect } from 'react';

import type { BoardType, CreateProjectBody } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import { Textarea } from '../../components/Textarea';
import { boardTypeLabels } from '../../i18n/enums';
import { useFormState } from '../../lib/useFormState';
import styles from './CreateProjectModal.module.css';

const BOARD_OPTIONS: readonly SelectOption<BoardType>[] = [
  { value: 'kanban', label: boardTypeLabels.kanban, description: 'Колонки без спринтов' },
  { value: 'scrum', label: boardTypeLabels.scrum, description: 'Спринты и бэклог' },
];

const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

type FormValues = {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly boardType: BoardType;
};

const INITIAL: FormValues = { key: '', name: '', description: '', boardType: 'kanban' };

export type CreateProjectModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (body: CreateProjectBody) => void;
  readonly isPending: boolean;
  readonly error?: string | null;
};

function validate(values: FormValues): Partial<Record<keyof FormValues, string>> {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (!KEY_PATTERN.test(values.key)) {
    errors.key = '2–10 символов: заглавные латинские буквы и цифры, начинается с буквы';
  }
  if (values.name.trim().length === 0) {
    errors.name = 'Введите название';
  }
  return errors;
}

export function CreateProjectModal({ open, onClose, onSubmit, isPending, error }: CreateProjectModalProps) {
  const form = useFormState<FormValues>(INITIAL, validate);

  useEffect(() => {
    if (!open) {
      form.reset();
    }
    // Сбрасываем только при закрытии: значения не должны теряться при ошибке сети.
  }, [open]);

  const handleSubmit = (): void => {
    if (!form.validate()) {
      return;
    }
    onSubmit({
      key: form.values.key.trim().toUpperCase(),
      name: form.values.name.trim(),
      description: form.values.description,
      boardType: form.values.boardType,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новый проект"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending}>
            Создать
          </Button>
        </>
      }
    >
      <div className={styles['form']}>
        <Input
          label="Ключ проекта"
          name="key"
          value={form.values.key}
          placeholder="DEMO"
          error={form.errors.key}
          hint="2–10 символов: заглавные латинские буквы и цифры"
          onChange={(event) => form.setValue('key', event.target.value.toUpperCase())}
          onBlur={() => form.handleBlur('key')}
        />
        <Input
          label="Название"
          name="name"
          value={form.values.name}
          error={form.errors.name}
          onChange={(event) => form.setValue('name', event.target.value)}
          onBlur={() => form.handleBlur('name')}
        />
        <Textarea
          label="Описание"
          name="description"
          rows={3}
          value={form.values.description}
          onChange={(event) => form.setValue('description', event.target.value)}
        />
        <Select
          label="Тип доски"
          options={BOARD_OPTIONS}
          value={form.values.boardType}
          onChange={(value) => form.setValue('boardType', value)}
        />
        {error ? (
          <p className={styles['error']} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
